import * as Sentry from "@sentry/react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { getSingleActiveCompetitionPath } from "@/features/competitions/redirect";
import BottomNav from "./components/BottomNav";
import OrgLayout from "./components/OrgLayout";
import { PushNotificationInit } from "./components/PushNotificationInit";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import AppleHealthConnect from "./pages/AppleHealthConnect";
import AuthCallback from "./pages/AuthCallback";
import CompetitionAdmin from "./pages/CompetitionAdmin";
import CompetitionCreate from "./pages/CompetitionCreate";
import CompetitionDetail from "./pages/CompetitionDetail";
import CompetitionSettings from "./pages/CompetitionSettings";
import CompetitionTeams from "./pages/CompetitionTeams";
import Dashboard from "./pages/Dashboard";
import Faq from "./pages/Faq";
import Friends from "./pages/Friends";
import FriendsAdd from "./pages/FriendsAdd";
import GoogleHealthCallback from "./pages/GoogleHealthCallback";
import HealthSetup from "./pages/HealthSetup";
import InviteLanding from "./pages/InviteLanding";
import Join from "./pages/Join";
import JoinCode from "./pages/JoinCode";
import Landing from "./pages/Landing";
import OrgCompetitions from "./pages/OrgCompetitions";
import OrgCreate from "./pages/OrgCreate";
import OrgDashboard from "./pages/OrgDashboard";
import OrgLeave from "./pages/OrgLeave";
import OrgMembers from "./pages/OrgMembers";
import OrgSettings from "./pages/OrgSettings";
import Privacy from "./pages/Privacy";
import RequestAccess from "./pages/RequestAccess";
import Settings from "./pages/Settings";
import SettingsGroups from "./pages/SettingsGroups";
import SettingsGroupsDetail from "./pages/SettingsGroupsDetail";
import SettingsGroupsNew from "./pages/SettingsGroupsNew";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import Support from "./pages/Support";
import TeamDetail from "./pages/TeamDetail";
import Terms from "./pages/Terms";

const ROUTE_NAMES: Record<string, string> = {
  "/": "Home",
  "/dashboard": "Dashboard",
  "/sign-in": "Sign in",
  "/sign-up": "Sign up",
  "/request-access": "Request access",
  "/privacy": "Privacy",
  "/terms": "Terms",
  "/faq": "FAQ",
  "/support": "Support",
};

function getRouteName(pathname: string): string {
  const exact = ROUTE_NAMES[pathname];
  if (exact) return exact;
  const cleaned = pathname.replace(/^\//, "");
  const segments = cleaned.split("/");
  if (segments[0] === "competition") {
    if (segments[1] === "create") return "Create competition";
    if (segments[1] === "admin") return "Competition admin";
    if (segments[1] === "settings") return "Competition settings";
    if (segments[1] === "teams") return "Competition teams";
    return "Competition";
  }
  if (segments[0] === "friends") {
    if (segments[1] === "add") return "Add friend";
    return "Friends";
  }
  if (segments[0] === "settings") return "Settings";
  if (segments[0] === "join") return "Join competition";
  if (segments[0] === "org") {
    if (segments[1] === "create") return "Create organization";
    if (segments[1] === "members") return "Organization members";
    if (segments[1] === "settings") return "Organization settings";
    if (segments[1] === "competitions") return "Organization competitions";
    if (segments[1] === "leave") return "Leave organization";
    return "Organization";
  }
  if (segments[0] === "apple-health") return "Apple Health";
  if (segments[0] === "google-health") return "Google Health";
  if (segments[0] === "health-setup") return "Health setup";
  if (segments[0] === "auth") return "Authentication";
  if (segments[0] === "invite") return "Invite";
  return cleaned.replace(/[-/]/g, " ") || "Page";
}

function AuthGuard() {
  const { user, isLoading, getPrefetchedCompetitionPath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const prevPathname = useRef(location.pathname);
  const checkedRedirect = useRef(false);

  const publicRoutes = [
    "/sign-in",
    "/sign-up",
    "/request-access",
    "/auth/callback",
    "/privacy",
    "/terms",
    "/faq",
    "/support",
    "/health-setup",
    "/",
  ];
  const inPublicRoute =
    publicRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/auth/") ||
    location.pathname.startsWith("/invite/") ||
    location.pathname === "/join" ||
    location.pathname.startsWith("/join/");

  const shouldRedirectToDashboard =
    user &&
    inPublicRoute &&
    location.pathname !== "/privacy" &&
    location.pathname !== "/terms" &&
    location.pathname !== "/faq" &&
    location.pathname !== "/support" &&
    location.pathname !== "/health-setup" &&
    location.pathname !== "/sign-in" &&
    location.pathname !== "/sign-up" &&
    !location.pathname.startsWith("/invite/") &&
    location.pathname !== "/join" &&
    !location.pathname.startsWith("/join/") &&
    !location.pathname.startsWith("/auth/");

  // Route focus management — focus main content on navigation
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      prevPathname.current = location.pathname;
      requestAnimationFrame(() => {
        mainRef.current?.focus({ preventScroll: true });
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isLoading) return;

    if (!user && !inPublicRoute) {
      navigate({
        to: "/sign-in",
        search: { redirect: location.href },
        replace: true,
      });
      return;
    }

    if (shouldRedirectToDashboard) {
      if (checkedRedirect.current) return;
      checkedRedirect.current = true;
      void (async () => {
        const prefetched = getPrefetchedCompetitionPath();
        const path = prefetched ? await prefetched : await getSingleActiveCompetitionPath();
        navigate({ to: path ?? "/dashboard", replace: true });
      })();
      return;
    }

    checkedRedirect.current = false;
  }, [
    isLoading,
    user,
    inPublicRoute,
    navigate,
    shouldRedirectToDashboard,
    getPrefetchedCompetitionPath,
    location.href,
  ]);

  // Also gate on the pending redirect itself — otherwise the public route
  // (e.g. Landing) renders for one frame between isLoading resolving and
  // the async redirect-away effect completing.
  if (isLoading || shouldRedirectToDashboard) return null;

  const showNav = user && !inPublicRoute;

  return (
    <div className="min-h-screen">
      {/* Skip-to-content link — first tabbable element */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:text-text focus:p-4 focus:rounded-[20px] focus:border focus:border-text focus:font-bold focus:text-[0.9375rem]"
      >
        Skip to content
      </a>

      {/* Screen reader route announcer */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {!isLoading && `Navigated to ${getRouteName(location.pathname)}`}
      </div>

      <main id="main-content" ref={mainRef} tabIndex={-1} className={showNav ? "pb-14" : ""}>
        <Outlet />
      </main>
      {showNav && <BottomNav />}
    </div>
  );
}

const SentryRoot = Sentry.withErrorBoundary(RootLayoutInner, {
  fallback: (
    <div
      style={{
        display: "flex",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "1rem",
        padding: "2rem",
      }}
    >
      <h1 style={{ fontSize: "1.25rem", fontWeight: 700 }}>Something went wrong</h1>
      <p style={{ fontSize: "0.875rem", textAlign: "center" }}>
        An unexpected error occurred. The team has been notified.
      </p>
    </div>
  ),
});

function RootLayoutInner() {
  return (
    <ToastProvider>
      <AuthProvider>
        <PushNotificationInit />
        <AuthGuard />
      </AuthProvider>
    </ToastProvider>
  );
}

function RootLayout() {
  return <SentryRoot />;
}

const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Landing,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignIn,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: SignUp,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    code: typeof search.code === "string" ? search.code : undefined,
  }),
});

const requestAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/request-access",
  component: RequestAccess,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallback,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: Privacy,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: Terms,
});

const faqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/faq",
  component: Faq,
});

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  component: Support,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: Dashboard,
});

const competitionCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/create",
  component: CompetitionCreate,
});

const competitionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/$id",
  component: CompetitionDetail,
});

const competitionAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/$id/admin",
  component: CompetitionAdmin,
});

const competitionSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/$id/settings",
  component: CompetitionSettings,
});

const competitionTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/$id/teams",
  component: CompetitionTeams,
});

const teamDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/$id/teams/$teamId",
  component: TeamDetail,
});

const friendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/friends",
  component: Friends,
});

const friendsAddRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/friends/add",
  component: FriendsAdd,
});

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join",
  component: Join,
});

const inviteLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$code",
  component: InviteLanding,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: Settings,
});

const settingsGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/groups",
  component: SettingsGroups,
});

const settingsGroupsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/groups/new",
  component: SettingsGroupsNew,
});

const settingsGroupsDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/groups/$groupId",
  component: SettingsGroupsDetail,
});

const joinCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join/$code",
  component: JoinCode,
});

const appleHealthConnectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apple-health/connect",
  component: AppleHealthConnect,
});

const googleHealthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/google-health/callback",
  component: GoogleHealthCallback,
});

const healthSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/health-setup",
  component: HealthSetup,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
});

const orgDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/org",
  component: () => (
    <OrgLayout>
      <OrgDashboard />
    </OrgLayout>
  ),
});

const orgCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/org/create",
  component: OrgCreate,
});

const orgMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/org/members",
  component: () => (
    <OrgLayout>
      <OrgMembers />
    </OrgLayout>
  ),
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/org/settings",
  component: () => (
    <OrgLayout>
      <OrgSettings />
    </OrgLayout>
  ),
});

const orgCompetitionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/org/competitions",
  component: () => (
    <OrgLayout>
      <OrgCompetitions />
    </OrgLayout>
  ),
});

const orgLeaveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/org/leave",
  component: () => (
    <OrgLayout>
      <OrgLeave />
    </OrgLayout>
  ),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  requestAccessRoute,
  authCallbackRoute,
  privacyRoute,
  termsRoute,
  faqRoute,
  supportRoute,
  dashboardRoute,
  competitionCreateRoute,
  competitionDetailRoute,
  competitionAdminRoute,
  competitionSettingsRoute,
  competitionTeamsRoute,
  teamDetailRoute,
  friendsRoute,
  friendsAddRoute,
  joinRoute,
  joinCodeRoute,
  inviteLandingRoute,
  settingsRoute,
  settingsGroupsRoute,
  settingsGroupsNewRoute,
  settingsGroupsDetailRoute,
  appleHealthConnectRoute,
  googleHealthCallbackRoute,
  healthSetupRoute,
  orgDashboardRoute,
  orgCreateRoute,
  orgMembersRoute,
  orgSettingsRoute,
  orgCompetitionsRoute,
  orgLeaveRoute,
]);

const router = createRouter({ routeTree });

export default function App() {
  return <RouterProvider router={router} />;
}
